import { ROUTES } from '@/lib/route-paths';
import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { identifyUser } from '@/components/posthog-provider';
import { ImpersonationBanner, TwoFactorGate } from '@/features/admin-terminal';
import { authRedirectSearchValue } from '@/lib/auth-redirect';
import { takeImpersonationExpiredNotice } from '@/lib/impersonation-token';
import { ensureSession, useSession } from '@/lib/session';

/**
 * `_admin` pathless layout — admin-only guard.
 *
 * `beforeLoad` resolves the session and:
 *  - redirects to `/sign-in` (with `?redirect=<intended>`) if not authed
 *  - redirects to `/dashboard` if authed but `user.role !== 'admin'`
 *
 * `TwoFactorGate` then enforces a 2FA setup-or-verify flow before any admin
 * UI renders. The NestJS `GlobalAdminGuard` is the authoritative check
 * (ADMIN_USER_IDS membership + valid `admin_2fa_verified` cookie); the gate
 * is the matching UX so admins see a verify prompt instead of an empty table
 * when the cookie isn't present.
 */
export const Route = createFileRoute('/_admin')({
  beforeLoad: async ({ location }) => {
    const session = await ensureSession();
    if (!session.user) {
      throw redirect({
        to: '/sign-in',
        search: { redirect: authRedirectSearchValue(location) },
      });
    }
    if (session.user.role !== 'admin' && session.user.role !== 'scoped-admin') {
      throw redirect({ to: ROUTES.dashboard });
    }
  },
  component: AdminLayout,
});

function AdminLayout() {
  const admin = useSession().data?.user;

  // The admin terminal never identified anyone, so its events and replays
  // went to whoever this browser last identified — after an impersonation,
  // the customer. Impersonation no longer writes there (see
  // posthog-impersonation.ts); this also repairs browsers that already
  // carry a customer's id.
  useEffect(() => {
    if (admin) {
      identifyUser(
        admin.id,
        { email: admin.email, name: admin.name },
        { isAdmin: true, organizationId: admin.organizationId }
      );
    }
  }, [admin]);

  // A tab whose impersonation ran out is sent here by `getRequestAuthToken`
  // instead of quietly carrying on as a different identity — say why.
  useEffect(() => {
    if (!takeImpersonationExpiredNotice()) return;
    // One tick later: `__root.tsx` mounts <Toaster /> AFTER <Outlet />, so
    // during this first commit it is not subscribed yet and a toast fired
    // now is dropped.
    setTimeout(() => {
      toast.info('Impersonation ended — the session expired after an hour.');
    }, 0);
  }, []);

  return (
    <>
      <ImpersonationBanner />
      <TwoFactorGate>
        <Outlet />
      </TwoFactorGate>
    </>
  );
}
